import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { Router, useLocation, useSearch } from "wouter";
import { DATE_LOCALES, localeFromPath, localizedPath, translateValue, translateTemplate, type Locale } from "@shared/i18n";

type LanguageState = {
  locale: Locale;
  dateLocale: string;
  fullPath: string;
  tx: <T>(value: T) => T;
  tf: (key: string, values: unknown[]) => string;
  href: (url: string) => string;
};
const LanguageContext = createContext<LanguageState | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [path] = useLocation();
  const search = useSearch();
  const locale = localeFromPath(path);
  const fullPath = path + (search ? `?${search}` : "");
  const value = useMemo<LanguageState>(() => ({
    locale, fullPath, dateLocale: DATE_LOCALES[locale],
    tx: value => translateValue(value, locale),
    tf: (key, values) => translateTemplate(key, values, locale),
    href: url => localizedPath(url, locale),
  }), [locale, fullPath]);
  useEffect(() => { document.documentElement.lang = locale; }, [locale]);
  return <LanguageContext.Provider value={value}><Router base={locale === "en" ? "" : `/${locale}`}>{children}</Router></LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("LanguageProvider is required");
  return context;
}
